import React, { useRef } from "react";
import styles from "./AvatarSelect.module.scss";
import classnames from "classnames";
import { Button } from "@swordsweeper/ssi-ui-component-library";
import UserAvatar from "@src/components/common/UserDisplay/UserAvatar";
import { UserAvatarSizes } from "@src/components/common/UserDisplay/UserAvatarSizes";

const AVAILABLE_BACKGROUND_COLORS = [
  "#FF6B35",
  "#FF98AA",
  "#A371AC",
  "#7C98B3",
  "#1DAFA0",
  "#FFC43D",
  "#B5B8C4",
];

interface AvatarSelectProps {
  userInitials?: string;
  userAvatarImageSrc?: string;
  backgroundColor?: string;
  onChangeBackgroundColor: (color: string) => void;
  onChangeAvatarImage: (file: File | null) => void;
}

export default function AvatarSelect({
  userInitials,
  userAvatarImageSrc,
  backgroundColor,
  onChangeBackgroundColor,
  onChangeAvatarImage,
}: AvatarSelectProps) {
  const imageUploadRef = useRef<HTMLInputElement>(null);

  const handleBeginImageUpload = () => {
    imageUploadRef.current?.click();
  };

  const handleImageSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      onChangeAvatarImage(event.target.files[0]);
      if (imageUploadRef.current) {
        imageUploadRef.current.value = "";
      }
    }
  };

  return (
    <div className="flex-row">
      <UserAvatar
        userInitials={userInitials}
        userAvatarImageSrc={userAvatarImageSrc}
        backgroundColor={backgroundColor}
        size={UserAvatarSizes.Large}
      />

      <div>
        <div className={styles.chooseColorContainer}>
          <div>Choose Color</div>
          <div className="flex-row spaced-content">
            {AVAILABLE_BACKGROUND_COLORS.map((hex) => (
              <div
                key={`available-background-color-${hex}`}
                id={`available-avatar-color-${hex.slice(1)}`} // slice to remove # for id
                className={classnames(styles.availableColor, {
                  [styles.selected]: backgroundColor === hex,
                })}
                style={{ backgroundColor: hex }}
                onClick={() => onChangeBackgroundColor(hex)}
                data-testid={`available-avatar-color-${hex.slice(1)}`}
              />
            ))}
          </div>
        </div>

        <div className="flex-row spaced-content margin-top-xl">
          <Button
            id="upload-avatar-image-button"
            className="btn btn-sm btn-bordered btn-primary"
            label="Upload Picture"
            variant="secondaryV2"
            onClick={handleBeginImageUpload}
            data-testid="upload-avatar-image-button"
          />
          <Button
            id="delete-avatar-image-button"
            className="btn btn-sm btn-themed-link btn-error"
            label="Delete Picture"
            disabled={!userAvatarImageSrc}
            onClick={() => onChangeAvatarImage(null)}
            data-testid="delete-avatar-image-button"
          />
          <input
            className={styles.hiddenImageInput}
            ref={imageUploadRef}
            type="file"
            onChange={handleImageSelected}
            accept="image/*"
          />
        </div>
      </div>
    </div>
  );
}
